import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "hooks/useAuth";
import { usePermissions } from "hooks/usePermissions";
import { useLicenseStatus } from "hooks/useLicenseStatus";
import { ROUTES, STORAGE_KEYS } from "constants/index";
import { Button } from "components/ui";
import { appLocalStorage, appSessionStorage } from "utils/storage";
import {
  DashboardOutlined,
  PersonOutline,
  SettingsOutlined,
  VerifiedOutlined,
  GroupOutlined,
  ListAltOutlined,
  AlternateEmail,
  BusinessOutlined,
} from "@mui/icons-material";
import { settingsService } from "services/settings.service";
import { useUIStore } from "store/uiStore";
import { MenuItem, PageId, PageWithPermissions } from "types/index";

interface MainLayoutProps {
  children: React.ReactNode;
}

export const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const { pages, isLoading: permissionsLoading } = usePermissions();
  const { companyData, isLoading: licenseLoading } = useLicenseStatus();
  const navigate = useNavigate();
  const { setLoading } = useUIStore();

  // Auto-open sidebar on desktop, keep closed on mobile
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsSidebarOpen(true);
      } else {
        setIsSidebarOpen(false);
      }
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLogout = async () => {
    navigate(ROUTES.LOGIN);
    await logout();
  };

  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);

  const isCompanyUser = Boolean(
    user && user.roleId && Number(user.roleId) !== 1
  );

  useEffect(() => {
    if (pages.length > 0 && (!isCompanyUser || !licenseLoading)) {
      const mapPageIdToRoute = (pageId: PageId): string | null => {
        switch (pageId) {
          case PageId.DASHBOARD:
            return ROUTES.DASHBOARD;
          case PageId.LICENSES:
            return ROUTES.LICENSES;
          case PageId.EMPLOYEES:
            return ROUTES.EMPLOYEES;
          case PageId.COMPANIES:
            return ROUTES.COMPANIES;
          case PageId.USERS:
            return ROUTES.USERS;
          case PageId.PACKAGES:
            return ROUTES.PACKAGES;
          case PageId.LOOKUPS:
            return ROUTES.LOOKUPS;
          case PageId.CONTACTUS:
            return ROUTES.CONTACTUS;
          default:
            return null;
        }
      };

      const iconMap: Record<string, React.ReactNode> = {
        [ROUTES.DASHBOARD]: <DashboardOutlined fontSize="small" />,
        [ROUTES.EMPLOYEES]: <GroupOutlined fontSize="small" />,
        [ROUTES.HOME]: <DashboardOutlined fontSize="small" />,
        [ROUTES.COMPANIES]: <DashboardOutlined fontSize="small" />,
        [ROUTES.USERS]: <GroupOutlined fontSize="small" />,
        [ROUTES.LICENSES]: <VerifiedOutlined fontSize="small" />,
        [ROUTES.PACKAGES]: <SettingsOutlined fontSize="small" />,
        [ROUTES.LOOKUPS]: <ListAltOutlined fontSize="small" />,
        [ROUTES.CONTACTUS]: <AlternateEmail fontSize="small" />,
      };

      const filteredPages = pages
        .filter((page: PageWithPermissions) => page.isActive && page.showInMenu)
        .sort(
          (a: PageWithPermissions, b: PageWithPermissions) => a.order - b.order
        );

      const menuItemsWithPermissions: MenuItem[] = filteredPages
        .map((page: PageWithPermissions) => {
          const path = mapPageIdToRoute(page.id);
          if (!path) return null;

          return {
            path,
            label: page.pageName,
            icon: iconMap[path] || <DashboardOutlined fontSize="small" />,
            pageId: page.id,
            permissions: page.actions,
          } as MenuItem;
        })
        .filter((item: MenuItem | null): item is MenuItem => item !== null);
      if (isCompanyUser && user?.company_id) {
        const companyProfileItem: MenuItem = {
          path: `/company/${user?.company_id}/profile`,
          label: "Company Profile",
          icon: <BusinessOutlined fontSize="small" />,
          permissions: [],
        };
        menuItemsWithPermissions.push(companyProfileItem);
      }
      setMenuItems(menuItemsWithPermissions);
    }
  }, [pages, isCompanyUser, companyData, licenseLoading]);

  const storedName = useMemo(() => {
    const fallback = user?.username || "User";
    const name = user?.name;
    return name || fallback;
  }, [user?.name, user?.username]);

  const avatarInitial = useMemo(() => {
    const letter = (storedName || "").trim().charAt(0);
    return letter ? letter.toUpperCase() : "A";
  }, [storedName]);

  return (
    <div className="min-h-screen bg-secondary-50 flex flex-col">
      <header className="bg-white border-b border-secondary-200 sticky top-0 z-40">
        <div className="px-3 sm:px-4 lg:px-6 xl:px-8">
          <div className="flex items-center justify-between h-14 sm:h-16">
            <div className="flex items-center gap-2 sm:gap-4">
              <button
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                className="p-2 rounded-lg hover:bg-secondary-100 transition-colors"
                aria-label="Toggle sidebar"
              >
                <svg
                  className="w-5 h-5 sm:w-6 sm:h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              </button>
              <Link to={ROUTES.HOME} className="flex items-center gap-2">
              <h1 className="text-lg sm:text-xl font-bold text-primary-600">YourQcard</h1>
              </Link>
            </div>

            <div className="flex items-center gap-2 sm:gap-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center font-semibold text-sm sm:text-base">
                  {avatarInitial}
                </div>
                <div className="hidden md:block">
                  <p className="text-sm font-medium text-secondary-900">
                    {user?.name}
                  </p>
                  <p className="text-xs text-secondary-500">{user?.username}</p>
                </div>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleLogout}
                className="px-2 sm:px-3"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  className="w-4 h-4 sm:mr-2"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6A2.25 2.25 0 005.25 5.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l3 3m0 0l-3 3m3-3H3"
                  />
                </svg>
                <span className="hidden sm:inline">Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile backdrop */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      <div className="flex min-h-[calc(100vh-3.5rem)] sm:min-h-[calc(100vh-4rem)] flex-1 relative">
        <aside
          className={`${
            isSidebarOpen 
              ? "translate-x-0" 
              : "-translate-x-full lg:translate-x-0"
          } ${
            isSidebarOpen ? "w-64" : "w-0 lg:w-64"
          } fixed lg:sticky top-14 sm:top-16 left-0 h-[calc(100vh-3.5rem)] sm:h-[calc(100vh-4rem)] bg-white border-r border-secondary-200 transition-all duration-300 overflow-hidden z-50 lg:z-auto overflow-y-auto`}
        >
          <nav className="p-3 sm:p-4 space-y-1 sm:space-y-2">
            {menuItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => {
                  // Close sidebar on mobile when navigating
                  if (window.innerWidth < 1024) {
                    setIsSidebarOpen(false);
                  }
                }}
                className="flex items-center gap-3 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg hover:bg-primary-50 text-secondary-700 hover:text-primary-600 transition-colors text-sm sm:text-base"
              >
                <span className="text-secondary-500 flex-shrink-0">{item.icon}</span>
                <span className="font-medium truncate">{item.label}</span>
              </Link>
            ))}
          </nav>
        </aside>

        <main className="flex-1 w-full lg:w-auto p-3 sm:p-4 md:p-6 lg:p-8">
          <div className="w-full max-w-full">{children}</div>
        </main>
      </div>
      <footer className="bg-white border-t border-secondary-200">
        <div className="px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2 text-sm text-secondary-600">
            <div>Powered by <span className="font-medium">IDS</span> © {new Date().getFullYear()}</div>
            <div className="text-secondary-400">All rights reserved.</div>
          </div>
        </div>
      </footer>
    </div>
  );
};
